import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { geo as G } from '@/data';
import { PageHeader } from '@/components/layout/PageHeader';
import { SectionCard } from '@/components/ui/Card';
import { Btn } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';

type MapMode = 'markers' | 'heatmap';
type SubFilter = 'all' | 'meth' | 'cannabis' | 'mdma' | 'ketamine' | 'heroin';

const subColors: Record<string, string> = {
  meth: '#0054A6', cannabis: '#1F8A5B', mdma: '#8A4FBE',
  ketamine: '#C77B0A', heroin: '#A50F1D',
};
const subNames: Record<string, { ko: string; mn: string }> = {
  meth: { ko: '메스암페타민', mn: 'Метамфетамин' },
  cannabis: { ko: '대마', mn: 'Каннабис' },
  mdma: { ko: 'MDMA', mn: 'MDMA' },
  ketamine: { ko: '케타민', mn: 'Кетамин' },
  heroin: { ko: '헤로인', mn: 'Героин' },
};

export default function Geo() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const ln = (ko: string, mn: string) => (lang === 'ko' ? ko : mn);

  const [mode, setMode] = useState<MapMode>('markers');
  const [subFilter, setSubFilter] = useState<SubFilter>('all');
  const [selected, setSelected] = useState<string>('bayanzurkh');

  const maxCount = Math.max(...G.districts.map((d) => d.count));

  function densityFill(count: number): string {
    const f = count / maxCount;
    if (f > 0.85) return '#0A4C8B';
    if (f > 0.6) return '#1B6FBE';
    if (f > 0.4) return '#5B8BC4';
    if (f > 0.2) return '#9CC0E4';
    return '#D5E3F2';
  }

  const visibleHotspots = G.hotspots.filter((h) => subFilter === 'all' || h.sub === subFilter);
  const sel = G.districts.find((d) => d.id === selected)!;
  const ranked = G.districts.slice().sort((a, b) => b.count - a.count);

  return (
    <div className="mp-page" data-screen-label="지리 분석">
      <PageHeader
        crumbs={[t('home'), t('mAnalysis'), t('mGeo')]}
        title={t('geoTitle')}
        titleExtra={<div className="mp-pagehead__meta"><span className="mp-pagehead__updated">{t('geoSub')}</span></div>}
        actions={<Btn icon="bi-download">{t('exportImage')}</Btn>}
      />

      <div className="mp-filterbar">
        <div className="mp-segmented" role="group">
          <button type="button" className={mode === 'markers' ? 'is-on' : ''} onClick={() => setMode('markers')}>
            <i className="bi bi-geo-alt-fill"></i> {t('viewMarkers')}
          </button>
          <button type="button" className={mode === 'heatmap' ? 'is-on' : ''} onClick={() => setMode('heatmap')}>
            <i className="bi bi-thermometer-half"></i> {t('viewHeatmap')}
          </button>
        </div>
        <label className="mp-filter">
          <span>{t('geoFilterSubstance')}</span>
          <select value={subFilter} onChange={(e) => setSubFilter(e.target.value as SubFilter)}>
            <option value="all">{t('all')}</option>
            {Object.keys(subNames).map((k) => (
              <option key={k} value={k}>{ln(subNames[k].ko, subNames[k].mn)}</option>
            ))}
          </select>
        </label>
        <span className="mp-filterbar__hint"><i className="bi bi-info-circle"></i> {t('clickRegionHint')}</span>
        <span className="mp-filterbar__count">{t('geoUpdated')}</span>
      </div>

      <div className="mp-grid mp-grid--graph">
        <div className="mp-card mp-mapwrap">
          <svg viewBox="0 0 920 470" className="mp-map" role="img" aria-label={t('geoTitle')}>
            <defs>
              {Object.entries(subColors).map(([k, c]) => (
                <radialGradient key={k} id={'heat-' + k} cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor={c} stopOpacity="0.62"></stop>
                  <stop offset="55%" stopColor={c} stopOpacity="0.28"></stop>
                  <stop offset="100%" stopColor={c} stopOpacity="0"></stop>
                </radialGradient>
              ))}
            </defs>

            <rect x="0" y="0" width="920" height="470" fill="#F4F7FA"></rect>

            <path d="M 30 350 Q 240 330 430 360 T 890 345" fill="none" stroke="#BBD6EC" strokeWidth="9" strokeLinecap="round" opacity="0.9"></path>
            <text x="820" y="335" fontSize="11" fill="#6FA8D6" fontStyle="italic">Туул гол</text>

            {G.districts.map((d) => {
              const isSel = d.id === selected;
              return (
                <g key={d.id} onClick={() => setSelected(d.id)} style={{ cursor: 'pointer' }}>
                  <polygon points={d.points}
                    fill={mode === 'heatmap' ? '#E9EEF3' : densityFill(d.count)}
                    fillOpacity={mode === 'heatmap' ? 1 : 0.92}
                    stroke={isSel ? '#FFCB05' : '#FFFFFF'}
                    strokeWidth={isSel ? 3.5 : 1.6}></polygon>
                  <text x={d.cx} y={d.cy - 4} textAnchor="middle" fontSize="12.5" fontWeight="600"
                    fill={mode === 'heatmap' || d.count / maxCount <= 0.4 ? '#1A3550' : '#FFFFFF'}>
                    {ln(d.ko, d.mn).replace('구', '').replace(/^.* /, '')}
                  </text>
                  <text x={d.cx} y={d.cy + 13} textAnchor="middle" fontSize="11"
                    fill={mode === 'heatmap' || d.count / maxCount <= 0.4 ? '#5B7A95' : 'rgba(255,255,255,0.85)'}>
                    {d.count} {t('cases')}
                  </text>
                </g>
              );
            })}

            {mode === 'heatmap' ? visibleHotspots.map((h, i) => {
              const rad = 50 + h.count * 4;
              return <circle key={i} cx={h.x} cy={h.y} r={rad} fill={'url(#heat-' + h.sub + ')'}></circle>;
            }) : null}

            {mode === 'markers' ? visibleHotspots.map((h, i) => {
              const r = 6 + h.count * 0.7;
              return (
                <g key={i} className="mp-map__marker">
                  <circle cx={h.x} cy={h.y} r={r} fill={subColors[h.sub]} fillOpacity="0.85" stroke="#fff" strokeWidth="1.8"></circle>
                  <text x={h.x} y={h.y + 3.5} textAnchor="middle" fontSize="10" fontWeight="700" fill="#fff">{h.count}</text>
                </g>
              );
            }) : null}

            <g transform="translate(872, 44)">
              <circle r="15" fill="#fff" stroke="#D0D1D2"></circle>
              <path d="M 0 -10 L 4 4 L 0 0 L -4 4 Z" fill="#0054A6"></path>
              <text x="0" y="-17" textAnchor="middle" fontSize="9" fill="#707070">N</text>
            </g>
            <g transform="translate(40, 440)">
              <line x1="0" y1="0" x2="80" y2="0" stroke="#5B7A95" strokeWidth="2"></line>
              <line x1="0" y1="-4" x2="0" y2="4" stroke="#5B7A95" strokeWidth="2"></line>
              <line x1="80" y1="-4" x2="80" y2="4" stroke="#5B7A95" strokeWidth="2"></line>
              <text x="40" y="-7" textAnchor="middle" fontSize="9.5" fill="#5B7A95">5 km</text>
            </g>
          </svg>

          <div className="mp-map__legend">
            {mode === 'markers' && subFilter === 'all' ? (
              <div className="mp-map__legendrow">
                {Object.keys(subColors).map((k) => (
                  <span key={k} className="mp-graph__legenditem">
                    <span className="mp-graph__dot" style={{ background: subColors[k], borderColor: subColors[k] }}></span>
                    {ln(subNames[k].ko, subNames[k].mn)}
                  </span>
                ))}
              </div>
            ) : (
              <div className="mp-map__legendrow">
                <span className="mp-graph__legendtitle">{t('legendDensity')}</span>
                <span className="mp-map__scale">
                  <span className="mp-map__scaletxt">{t('densityLow')}</span>
                  <span className="mp-map__swatch" style={{ background: '#D5E3F2' }}></span>
                  <span className="mp-map__swatch" style={{ background: '#9CC0E4' }}></span>
                  <span className="mp-map__swatch" style={{ background: '#5B8BC4' }}></span>
                  <span className="mp-map__swatch" style={{ background: '#1B6FBE' }}></span>
                  <span className="mp-map__swatch" style={{ background: '#0A4C8B' }}></span>
                  <span className="mp-map__scaletxt">{t('densityHigh')}</span>
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="mp-stack">
          <SectionCard title={t('selectedRegion')}>
            <div className="mp-nodedetail__name">{ln(sel.ko, sel.mn)}</div>
            <div className="mp-georate">
              <div className="mp-georate__item">
                <span className="mp-georate__num">{sel.count}</span>
                <span className="mp-georate__lbl">{t('regionIncidents')}</span>
              </div>
              <div className="mp-georate__item">
                <span className="mp-georate__num">{sel.active}</span>
                <span className="mp-georate__lbl">{t('regionActive')}</span>
              </div>
              <div className="mp-georate__item">
                <span className="mp-georate__num">{sel.arrest}</span>
                <span className="mp-georate__lbl">{t('regionArrest')}</span>
              </div>
            </div>
            <div className="mp-geotop">
              <span className="mp-graph__dot" style={{ background: subColors[sel.top], borderColor: subColors[sel.top] }}></span>
              {t('topSubstance')}: <strong>{ln(subNames[sel.top].ko, subNames[sel.top].mn)}</strong>
            </div>
          </SectionCard>

          <SectionCard title={t('hotspotRank')} flush>
            <ol className="mp-ranklist">
              {ranked.map((d, i) => (
                <li key={d.id} className={d.id === selected ? 'is-current' : ''} onClick={() => setSelected(d.id)}>
                  <span className="mp-ranklist__no">{i + 1}</span>
                  <span className="mp-ranklist__name">{ln(d.ko, d.mn)}</span>
                  <span className="mp-ranklist__bar">
                    <span style={{ width: (d.count / maxCount * 100) + '%', background: densityFill(d.count) }}></span>
                  </span>
                  <span className="mp-ranklist__val">{d.count}</span>
                </li>
              ))}
            </ol>
          </SectionCard>

          <SectionCard title={t('routeTitle')} flush>
            <div className="mp-card__sub">{t('routeSub')}</div>
            <ul className="mp-routelist">
              {G.routes.map((r, i) => (
                <li key={i}>
                  <i className="bi bi-signpost-split"></i>
                  <div className="mp-routelist__text">
                    <span className="mp-routelist__name">{ln(r.ko, r.mn)}</span>
                    <span className="mp-routelist__via">{r.via}</span>
                  </div>
                  <Badge tone="neutral">{r.count}{t('cases')}</Badge>
                </li>
              ))}
            </ul>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
